import {SxProps, Theme} from '@mui/material';
import theme from 'src/theme';

export const weekMatrixSx: SxProps<Theme> = {
    backgroundColor: 'white',
    borderRadius: '12px',
    // The grid inside needs 100px + 24x40px = 1060px, so from roughly 1100px down it used to be
    // clipped by the `overflow: hidden` that was here and the hours past the cut were unreachable.
    // This element is now the scrollport instead. Vertical overflow stays hidden so the rounded
    // corners still clip, and the scrollbar only appears at the widths that were cut off before.
    overflowX: 'auto',
    overflowY: 'hidden',
    // And it is allowed to be narrower than what it holds. Without this a scrollport simply grows
    // to its content's 1060px: there is then nothing to scroll inside it, and the page clips the
    // overflow instead - the hours past the fifth were on the screen's far side with no way back.
    minWidth: 0,
    /**
     * Never wider than the screen, whatever the ancestors do.
     *
     * `100%` is only as good as the chain above it: one element anywhere between here and the app
     * root that refuses to shrink - a flex item with the default `min-width: auto`, say - and `100%`
     * resolves to the matrix's own 1060px, which is exactly how this stayed unscrollable through
     * three attempts. `100vw` answers to the viewport instead, so the scrollport is bounded even if
     * something above it is not.
     */
    maxWidth: '100vw',
    /**
     * A finger's drag scrolls this; it does not select cells.
     *
     * The cells listen for `mousedown` and drag to select a range, and a touch that the browser has
     * not been told about becomes exactly that - so on a phone, and in a desktop browser's device
     * emulation, every attempt to scroll the week painted a selection instead and the grid sat
     * still. `pan-x` claims the horizontal gesture for scrolling before the handlers ever see it.
     */
    touchAction: 'pan-x',
    /**
     * The scrollbar stays visible.
     *
     * macOS hides overlay scrollbars until something scrolls, which on a grid people scroll by
     * dragging means it is never shown - there was nothing on screen to say the week continued, and
     * nothing to grab. Thin, quiet, and always there.
     */
    scrollbarWidth: 'thin',
    '&::-webkit-scrollbar': {
        height: '10px',
    },
    '&::-webkit-scrollbar-track': {
        background: '#F5F5F7',
        borderRadius: '0 0 12px 12px',
    },
    '&::-webkit-scrollbar-thumb': {
        background: '#BCC3E4',
        borderRadius: '6px',
        border: '2px solid #F5F5F7',
        '&:hover': {
            background: '#9090B0',
        },
    },
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
    border: '1px solid #EDEDF2',
};

/**
 * The track that actually scrolls.
 *
 * One child of the scrollport holding the header and all seven days, sized once to the week's real
 * width. Before this the scrollport had eight children that each claimed `max-content` on their own,
 * which leaves the scrollable area to be inferred rather than stated - and anything that shrank one
 * of them shrank the scroll with it. Here the width is declared in one place: 100px for the day
 * column plus twenty-four hours at 40px.
 */
export const matrixTrackSx: SxProps<Theme> = {
    display: 'block',
    width: 'max-content',
    minWidth: '100%',
};

/**
 * The frame the matrix sits in - it carries the drag handler, and nothing else.
 *
 * It has to be allowed to shrink for the same reason the scrollport does: a block that refuses to be
 * narrower than its contents passes the full 1060px down and the scroll never engages.
 */
export const matrixFrameSx: SxProps<Theme> = {
    userSelect: 'none',
    minWidth: 0,
    maxWidth: '100%',
};

/**
 * The controls that move the week, and the only reliable way to do it.
 *
 * The scrollport has been genuinely scrollable for a while - 1060px of grid in ~370px of screen,
 * `overflow-x: auto` - and it still read as frozen, because nothing on any device could ask it to
 * move: macOS hides the scrollbar until something scrolls, a mouse wheel only scrolls vertically,
 * and dragging inside the grid is how a range of cells is selected, so the drag never reaches the
 * scroller. Two buttons and the hour range they are moving through settle it everywhere at once.
 *
 * Rendered only while there is somewhere to go, so a wide screen shows nothing new.
 */
export const scrollControlsSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '8px',
    marginBottom: '8px',
};

export const scrollHintSx: SxProps<Theme> = {
    fontSize: '12px',
    fontWeight: 600,
    color: '#666687',
    marginRight: 'auto',
    fontVariantNumeric: 'tabular-nums',
};

export const scrollButtonSx: SxProps<Theme> = {
    width: '36px',
    height: '36px',
    fontSize: '14px',
    color: '#32324D',
    backgroundColor: '#F5F5F7',
    border: '1px solid #EDEDF2',
    borderRadius: '8px',
    '&:hover': {
        backgroundColor: '#EDEDF2',
    },
    '&.Mui-disabled': {
        color: '#C0C0CF',
        backgroundColor: '#FAFAFC',
    },
    // A finger's target below a desktop; the pointer keeps the compact square.
    [theme.breakpoints.down('md')]: {
        width: '44px',
        height: '44px',
    },
};

export const weekHeaderSx: SxProps<Theme> = {
    display: 'grid',
    /**
     * Hour columns with a floor of 40px and no ceiling, and the two widths that make both screens
     * behave.
     *
     * `minmax(40px, 1fr)` is what the week has always used and what a wide screen needs: the
     * twenty-four hours share the row, so the matrix fills its card instead of huddling at the left
     * in a 1060px block. Replacing it with a fixed `40px` to force an overflow was a mistake - it
     * bought nothing (the 40px floor already overflows a narrow port) and it squeezed the desktop.
     *
     * `width: max-content` sizes the tracks at that floor when the port is narrower than the week,
     * which is the 1060px the scrollport scrolls. `min-width: 100%` then stretches the grid back to
     * the port when the port is the wider of the two, and the `1fr` maximum hands the spare width to
     * the hours. One rule, both screens, and nothing to switch on a breakpoint.
     */
    gridTemplateColumns: '100px repeat(24, minmax(40px, 1fr))',
    width: 'max-content',
    minWidth: '100%',
    backgroundColor: '#363656',
    color: 'white',
};

export const cornerCellSx: SxProps<Theme> = {
    padding: '16px 8px',
    fontWeight: 600,
    fontSize: '12px',
    textAlign: 'center',
    backgroundColor: '#363656',
    borderRight: '1px solid #525272',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '50px',
    // Pin the label column while the hours scroll under it. The cell is already opaque.
    // Gated below md so the desktop paint order (a hovered cell lifts itself to z-index 10)
    // is left exactly as it is.
    [theme.breakpoints.down('md')]: {
        position: 'sticky',
        left: 0,
        zIndex: 11,
    },
};

export const hourHeaderSx: SxProps<Theme> = {
    padding: '16px 4px',
    textAlign: 'center',
    fontWeight: 500,
    fontSize: '12px',
    borderRight: '1px solid #525272',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '50px',
    minWidth: '40px',
};

export const weekRowSx: SxProps<Theme> = {
    display: 'grid',
    // The same track list and the same two widths as the header, so a day's cells stay under their
    // hour at every width and the row separator runs the full scrolled width rather than stopping at
    // the visible edge.
    gridTemplateColumns: '100px repeat(24, minmax(40px, 1fr))',
    width: 'max-content',
    minWidth: '100%',
    borderBottom: '1px solid #EDEDF2',
    '&:last-child': {
        borderBottom: 'none',
    },
};

export const dayNameSx: SxProps<Theme> = {
    padding: '16px 12px',
    backgroundColor: '#F5F5F7',
    fontWeight: 600,
    color: '#32324D',
    textAlign: 'center',
    borderRight: '2px solid #EDEDF2',
    borderTop: '1px solid #EDEDF2',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '50px',
    fontSize: '13px',
    // Stays visible while the hours scroll under it, matching the corner cell above.
    [theme.breakpoints.down('md')]: {
        position: 'sticky',
        left: 0,
        zIndex: 11,
    },
};

export const timeCellSx: SxProps<Theme> = {
    padding: '16px 4px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    borderRight: '3px solid #EDEDF2',
    borderTop: '1px solid #EDEDF2',
    minHeight: '50px',
    minWidth: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    '&.has-price': {
        backgroundColor: '#F5F5F7',
    },
    '&.no-price': {
        backgroundColor: '#F5F5F7',
    },
};

export const priceValueSx: SxProps<Theme> = {
    background: 'linear-gradient(135deg, #007fdc, #006dc7)',
    color: 'white',
    padding: '6px 8px',
    borderRadius: '6px',
    fontSize: '11px',
    fontWeight: 600,
    minWidth: '40px',
    boxShadow: '0 2px 4px rgba(0, 127, 220, 0.2)',
};